import { initializeApp } from 'firebase/app';
import { getAnalytics, isSupported as analyticsSupported } from 'firebase/analytics';
import { getAuth, connectAuthEmulator, getRedirectResult } from 'firebase/auth';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

const useEmulators = process.env.REACT_APP_USE_EMULATORS === 'true';

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// App Check: attest requests with reCAPTCHA Enterprise. Skipped under emulators
// and when no site key is configured. Failures here are non-fatal while App
// Check enforcement is off.
const appCheckSiteKey = process.env.REACT_APP_APPCHECK_SITE_KEY;
if (!useEmulators && appCheckSiteKey && typeof window !== 'undefined') {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (_) {
    /* non-fatal */
  }
}

// Analytics only initializes in supported (non-emulator, browser) contexts.
let analytics = null;
if (!useEmulators) {
  analyticsSupported()
    .then((ok) => {
      if (ok) analytics = getAnalytics(app);
    })
    .catch(() => {});
}

if (useEmulators) {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
}

// Completes a pending redirect-based sign-in (the deployed Google flow).
// Resolves to { user, error } and never rejects, so consuming it can't raise an
// unhandled rejection before the UI attaches a handler. The important case is
// error.code === 'auth/multi-factor-auth-required': an MFA-enrolled account
// surfaces its second-factor challenge here (after the redirect reloads the
// page), not from the sign-in click handler. The sign-in UI awaits this to run
// that challenge. On the happy path the auth state listener picks up the user.
export const redirectSignInResult =
  typeof window !== 'undefined'
    ? getRedirectResult(auth)
        .then((cred) => ({ user: cred?.user || null, error: null }))
        .catch((error) => {
          if (error?.code !== 'auth/multi-factor-auth-required') {
            // eslint-disable-next-line no-console
            console.warn('Redirect sign-in error:', error?.code || error?.message || error);
          }
          return { user: null, error };
        })
    : Promise.resolve({ user: null, error: null });

export { app, analytics, auth };
